// Utility functions for drag and drop player rearrangement
//
// This module provides functionality to allow users to drag and drop players
// to rearrange matchups in the first round, but only before any matches have
// advanced (excluding BYE matches). This gives tournament organizers flexibility
// to adjust initial pairings while maintaining bracket integrity.

export const canRearrangePlayers = (bracket) => {
  // Check if any non-BYE matches have been started or finished
  return !bracket.some(match => {
    // Skip BYE matches (they can always be rearranged)
    if (match.p1?.name === "BYE" || match.p2?.name === "BYE") {
      return false;
    }

    // Skip matches that are waiting for other matches (Winner of, Loser of)
    if (match.p1?.name?.includes("Winner of") ||
        match.p1?.name?.includes("Loser of") ||
        match.p2?.name?.includes("Winner of") ||
        match.p2?.name?.includes("Loser of")) {
      return false;
    }

    // Check if match has been started or finished
    return match.started || match.finished;
  });
};


export const getFirstRoundMatches = (bracket) => {
  return bracket.filter(match => 
    match.round === 1 && 
    match.p1?.name && 
    match.p2?.name &&
    !match.p1.name.includes("Winner of") &&
    !match.p1.name.includes("Loser of") &&
    !match.p2.name.includes("Winner of") &&
    !match.p2.name.includes("Loser of")
  );
};

export const getAllInitialPlayers = (bracket) => {
  const players = new Set();
  
  bracket.forEach(match => {
    if (match.round === 1) {
      if (match.p1?.name && 
          !match.p1.name.includes("Winner of") && 
          !match.p1.name.includes("Loser of")) {
        players.add(match.p1.name);
      }
      if (match.p2?.name && 
          !match.p2.name.includes("Winner of") && 
          !match.p2.name.includes("Loser of")) {
        players.add(match.p2.name);
      }
    }
  });
  
  return Array.from(players);
};

export const swapPlayersInMatches = (bracket, player1, player2) => {
  // First pass: update all player name references throughout the bracket
  const newBracket = bracket.map(match => {
    const updatedMatch = { ...match };
    
    // Update p1 player name and ref
    if (match.p1?.name === player1) {
      updatedMatch.p1 = { ...updatedMatch.p1, name: player2 };
      // Also update ref if it was referencing the old name
      if (updatedMatch.p1.ref === player1) {
        updatedMatch.p1.ref = player2;
      }
    } else if (match.p1?.name === player2) {
      updatedMatch.p1 = { ...updatedMatch.p1, name: player1 };
      // Also update ref if it was referencing the old name
      if (updatedMatch.p1.ref === player2) {
        updatedMatch.p1.ref = player1;
      }
    }
    
    // Update p2 player name and ref  
    if (match.p2?.name === player1) {
      updatedMatch.p2 = { ...updatedMatch.p2, name: player2 };
      // Also update ref if it was referencing the old name
      if (updatedMatch.p2.ref === player1) {
        updatedMatch.p2.ref = player2;
      }
    } else if (match.p2?.name === player2) {
      updatedMatch.p2 = { ...updatedMatch.p2, name: player1 };
      // Also update ref if it was referencing the old name
      if (updatedMatch.p2.ref === player2) {
        updatedMatch.p2.ref = player1;
      }
    }
    
    // Update winner in any finished match
    if (updatedMatch.finished && updatedMatch.winner) {
      if (updatedMatch.winner === player1) {
        updatedMatch.winner = player2;
      } else if (updatedMatch.winner === player2) {
        updatedMatch.winner = player1;
      }
    }
    
    // Special handling for first round matches - reset auto-advanced matches if needed
    if (match.round === 1) {
      if (updatedMatch.status === "Auto-advanced (BYE)" && 
          updatedMatch.p1.name !== "BYE" && 
          updatedMatch.p2.name !== "BYE") {
        updatedMatch.winner = "";
        updatedMatch.finished = false;
        updatedMatch.status = "Pending";
        updatedMatch.score1 = "0";
        updatedMatch.score2 = "0";
      }
    }
    
    return updatedMatch;
  });
  
  return newBracket;
};

export const finalizeSwap = (bracket, tournamentManager) => {
  // After swapping players, we need to finalize BYE matches and force complete re-propagation
  if (tournamentManager && tournamentManager.finalizeByeMatches) {
    tournamentManager.finalizeByeMatches(bracket);
  }
  
  // Force complete winner propagation to update all auto-advanced player names
  if (tournamentManager && tournamentManager.propagateWinners) {
    tournamentManager.propagateWinners(bracket);
  }
  
  return bracket;
};